import { useState, useEffect } from 'react';
import config from '../../config';
import { apiService } from '../../services/apiService';
import Button from '../ui/Button';

// Semantic version comparison function
function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
}

export default function VersionChecker() {
  const [updateType, setUpdateType] = useState<'none' | 'optional' | 'mandatory'>('none');
  const [serverVersion, setServerVersion] = useState('');
  const [minVersion, setMinVersion] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const clientVersion = config.APP.VERSION;

  const checkVersion = async () => {
    const cacheKey = 'system_settings_cache';
    const lastCheckKey = 'system_settings_last_check';
    const checkInterval = config.APP.VERSION_CHECK_INTERVAL ?? 10800000;

    try {
      const now = Date.now();
      const lastCheck = localStorage.getItem(lastCheckKey);
      const cachedData = localStorage.getItem(cacheKey);

      let settings;

      if (lastCheck && cachedData && now - parseInt(lastCheck, 10) < checkInterval) {
        settings = JSON.parse(cachedData);
      } else {
        settings = await apiService.getSystemSettings();
        localStorage.setItem(cacheKey, JSON.stringify(settings));
        localStorage.setItem(lastCheckKey, now.toString());
      }

      setServerVersion(settings.current_version);
      setMinVersion(settings.min_version);
      setUpdateMessage(settings.update_message);

      // Check if client is below minimum required version
      if (compareVersions(clientVersion, settings.min_version) < 0) {
        if (settings.is_mandatory) {
          setUpdateType('mandatory');
          return;
        }
      }

      // Check if client is below target version
      if (compareVersions(clientVersion, settings.current_version) < 0) {
        if (settings.is_mandatory) {
          setUpdateType('mandatory');
        } else {
          setUpdateType('optional');
        }
      } else {
        setUpdateType('none');
      }
    } catch (error) {
      console.error('Failed to fetch system version settings:', error);
    }
  };

  useEffect(() => {
    checkVersion();
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      // 1. Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // 2. Clear all cache storage
      if ('caches' in window) {
        const keys = await caches.keys();
        for (const key of keys) {
          await caches.delete(key);
        }
      }

      // Hard reload with cache busting
      window.location.reload();
    } catch (err) {
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  // If client is up to date, or optional update is dismissed, render nothing
  if (updateType === 'none' || (updateType === 'optional' && isDismissed)) {
    return null;
  }

  if (updateType === 'mandatory') {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/80 backdrop-blur-md p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl border border-gray-100 animate-in fade-in zoom-in duration-300">
          <div className="flex flex-col items-center text-center">
            {/* Animated Update Icon */}
            <div className="relative flex items-center justify-center w-20 h-20 mb-6">
              {/* Pulsating background glow */}
              <div className="absolute inset-0 rounded-full bg-blue-500/20 blur-xl animate-pulse"></div>

              {/* Techy rotating outer ring */}
              <div className="absolute inset-0 rounded-2xl border-2 border-dashed border-blue-500/30 animate-spin [animation-duration:15s]"></div>

              {/* Premium Blue core container */}
              <div className="relative w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/35 border border-blue-400/20">
                <svg className="w-7 h-7 text-white animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">Critical Update Required</h2>
            <p className="text-sm text-gray-500 mb-6">
              A mandatory update is required to continue using PromptShare. This version resolves important cached asset issues, login failures, and photo loading.
            </p>

            {/* Custom Admin Update Message Box */}
            {updateMessage && (
              <div className="w-full bg-primary-50/50 rounded-xl p-4 border border-primary-100 text-left mb-6 text-sm text-primary-800">
                <strong className="block font-semibold mb-1">What's New in v{serverVersion}:</strong>
                <p className="whitespace-pre-wrap">{updateMessage}</p>
              </div>
            )}

            <div className="w-full space-y-3">
              <Button
                onClick={handleUpdate}
                disabled={isUpdating}
                isLoading={isUpdating}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 rounded-xl shadow-lg shadow-primary-500/20"
              >
                Refresh & Update Now
              </Button>
              <div className="text-xs text-gray-400">
                Current Version: v{clientVersion} • Min Required: v{minVersion} • Target Version: v{serverVersion}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Optional Update Banner
  return (
    <div className="fixed bottom-4 right-4 z-[9998] max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-150 p-5 animate-in slide-in-from-bottom-5 fade-in duration-300">
      <div className="flex gap-4">
        <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 shadow-sm relative overflow-hidden">
          <div className="absolute inset-0 bg-blue-600/5 animate-pulse"></div>
          <svg className="w-5 h-5 text-blue-600 relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-900">New Version Available!</h4>
          <p className="text-xs text-gray-500 mt-1">
            An optional update (v{serverVersion}) is available. It is recommended to update for optimal performance.
          </p>
          {updateMessage && (
            <p className="text-xs text-primary-800 bg-primary-50/50 rounded-lg p-2 mt-2 border border-primary-50/80">
              {updateMessage}
            </p>
          )}
          <div className="flex gap-3 mt-4">
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              isLoading={isUpdating}
              size="sm"
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              Update Now
            </Button>
            <Button
              onClick={handleDismiss}
              disabled={isUpdating}
              variant="outline"
              size="sm"
            >
              Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
